import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/api';
import { getApiErrorMessage, getErrorCode } from '../utils/apiError';
import type { AuthStore, LoginRequest, User, AuthError } from '../types';

// Extend the store type locally to include SUPER_ADMIN org-scoping fields
export type ExtendedAuthStore = AuthStore & {
  scopedOrgId: number | null;
  setScopedOrg: (orgId: number | null) => void;
};

export const useAuthStore = create<ExtendedAuthStore>()(
  persist(
    (set, get) => ({
      // State
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      // SUPER_ADMIN org scoping
      scopedOrgId: null,

      // Actions
      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.login(credentials);

          // Decode JWT to get user info (including M2 org+plan claims)
          const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));

          set({
            token: response.access_token,
            refreshToken: response.refresh_token,
            user: {
              id: tokenPayload.sub,
              email: tokenPayload.email,
              role: tokenPayload.role,
              organisationId: tokenPayload.org ?? null,
              organisationPlan: tokenPayload.plan ?? null,
              organisationName: tokenPayload.orgName ?? null,
              isRootOrg: tokenPayload.rootOrg === true,
            } as User,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const authError: AuthError = {
            message: getApiErrorMessage(error, 'Login failed'),
            code: getErrorCode(error) || 'LOGIN_ERROR',
          };

          set({
            isLoading: false,
            error: authError,
            isAuthenticated: false,
            token: null,
            refreshToken: null,
            user: null,
          });

          throw authError;
        }
      },

      logout: async () => {
        const { token } = get();

        try {
          if (token) {
            await authApi.logout();
          }
        } catch {
          console.warn('Logout API call failed:');
        } finally {
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            error: null,
            scopedOrgId: null,
          });
        }
      },

      refreshAuthToken: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await authApi.refresh({ refreshToken: refreshToken });

          // Re-decode the new access token to keep org+plan claims fresh
          const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
          const { user: currentUser } = get();

          set({
            token: response.access_token,
            refreshToken: response.refresh_token,
            user: currentUser
              ? {
                  ...currentUser,
                  organisationId: tokenPayload.org ?? currentUser.organisationId,
                  organisationPlan: tokenPayload.plan ?? currentUser.organisationPlan,
                  organisationName: tokenPayload.orgName ?? currentUser.organisationName,
                  isRootOrg: tokenPayload.rootOrg === true,
                }
              : currentUser,
            error: null,
          });
        } catch (error: unknown) {
          // If refresh fails, logout user
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            error: null,
          });

          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      // Root-org users: switch the active org context (null = global view)
      setScopedOrg: (orgId: number | null) => {
        const { user } = get();
        if (!user?.isRootOrg && user?.role !== 'SUPER_ADMIN') return;
        set({ scopedOrgId: orgId });
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        scopedOrgId: state.scopedOrgId,
      }),
    }
  )
);
